import { useState, useRef, useEffect } from 'react';
import { Tabs, TabHeader, TabPanel } from '@wk/components-react16';
import CsbErrorBoundary from 'components/CsbErrorBoudary/CsbErrorBoundary';
import style from './ImportOrchestrationModal.module.scss';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { closeModal } from 'redux/modal/ModalSlice';
import { MODAL_ACTIONS } from 'interfaces/modals/closeModalInfo.interface';
import { useNavigate } from 'react-router-dom';
import { ButtonField } from '@wk/components-v3-react16';
import {
  clearImportModalSelectedFile,
  selectAllWorkflowsNames,
  selectSelectedWorkflow,
  setImportModalSelectedFile,
} from 'redux/runOrchestrations/RunOrchestrationsSlice';
import {
  isCorrectFileType,
  xmlFileReader,
} from 'pages/RunOrchestration/RunOrchestrationBanner/components/RunOrchestrationButtons/utils/helpers';
import { showToastAndClose } from 'utils/commonFunctions/CommonFunctions';
import DragAndDropFileUpload from '../../../DragAndDropFileUpload/DragAndDropFileUpload';
import {
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

const FILE_UPLOAD_SECTION = 'file-upload-section';

export interface IImportOrchestrationModal {
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>; // Add watch to monitor form changes
}

const ImportOrchestrationModal = ({
  register,
  setValue,
  watch,
}: IImportOrchestrationModal) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedWorkflow = useAppSelector(selectSelectedWorkflow);
  const workflowsNames = useAppSelector(selectAllWorkflowsNames);
  const [activeTabId, setActiveTabId] = useState(selectedWorkflow ? 1 : 0);
  const [clearContent, setClearContent] = useState(false);
  const CONTENT_FIELD_NAME = 'content';
  const UPDATE_CONTENT_FIELD_NAME = 'updateContent';
  register(UPDATE_CONTENT_FIELD_NAME);
  register('selectedTab', { value: selectedWorkflow ? 1 : 0 });
  const uploadedNewContent = watch(CONTENT_FIELD_NAME);
  const uploadedUpdateContent = watch(UPDATE_CONTENT_FIELD_NAME);

  useEffect(() => {
    if (
      (uploadedNewContent && uploadedNewContent.length > 0) ||
      (uploadedUpdateContent && uploadedUpdateContent.length > 0)
    ) {
      const isUpdate = activeTabId === 1;
      const file: File = isUpdate
        ? uploadedUpdateContent[0]
        : uploadedNewContent[0];
      handleImportFile(file, isUpdate);
    }
  }, [uploadedNewContent, uploadedUpdateContent, setValue, dispatch]);

  useEffect(() => {
    return () => {
      setValue(CONTENT_FIELD_NAME, []);
      setValue(UPDATE_CONTENT_FIELD_NAME, []);
      dispatch(clearImportModalSelectedFile());
    };
  }, []);

  const handleTabChange = (tabId: number) => {
    setActiveTabId(tabId);
    setValue('selectedTab', tabId);
  };

  const handleGoToRunOrchestration = () => {
    dispatch(
      closeModal({
        id: 'import-orchestration-modal',
        action: MODAL_ACTIONS.cancel,
      })
    );

    navigate('/run-orchestrations');
  };

  const handleImportFile = async (file: File, isUpdate: boolean) => {
    let validationError: string | null = null;
    if (isUpdate) {
      validationError = await validateImportFile(file, selectedWorkflow);
    } else {
      validationError = await validateNewImportFile(file, workflowsNames);
    }
    if (validationError) {
      showToastAndClose('error', 'informationToast', () => {}, dispatch, {
        text: validationError,
      });
      setClearContent(true);
      if (isUpdate) {
        setValue(UPDATE_CONTENT_FIELD_NAME, []);
      } else {
        setValue(CONTENT_FIELD_NAME, []);
      }
      setTimeout(() => {
        setClearContent(false);
      }, 50);
      return;
    }
    dispatch(setImportModalSelectedFile(file.name.replace('.xml', '')));
  };

  return (
    <CsbErrorBoundary>
      <div
        className={style['import-modal-container']}
        data-testid="ImportOrchestrationModal"
      >
        <span className={style['header']}>Select file</span>
        <div className={style['content-container']}>
          <Tabs
            onUserRequest={(selectedTab: number) =>
              handleTabChange(selectedTab)
            }
            selectedTab={activeTabId}
          >
            <TabHeader slot="tabHeader">
              Add a new orchestration template
            </TabHeader>
            <TabHeader slot="tabHeader">
              Update the selected orchestration
            </TabHeader>

            <TabPanel slot="tabPanel">
              <div className={style['tab-content']} data-testid="NewImportTab">
                <div className={style[FILE_UPLOAD_SECTION]}>
                  <DragAndDropFileUpload
                    register={register}
                    reset={clearContent}
                    setValue={setValue}
                    filesLimit={1}
                    fileName={CONTENT_FIELD_NAME}
                  />
                </div>
              </div>
            </TabPanel>

            <TabPanel slot="tabPanel">
              <div
                className={style['tab-content']}
                data-testid="UpdateImportTab"
              >
                {!selectedWorkflow ? (
                  <div className={style['update-instructions-container']}>
                    <div className={style['update-instructions']}>
                      <p className={style['instructions-text-bold']}>
                        To update an existing orchestration
                      </p>
                      <p className={style['instructions-text']}>
                        select it first on the Run Orchestration screen, and
                        then click 'Import'
                      </p>
                    </div>
                    <ButtonField type="primary">
                      <button
                        type="button"
                        flow-id="primary-button"
                        onClick={handleGoToRunOrchestration}
                        data-testid="GoToRunOrchestrationButton"
                      >
                        Go to the Run Orchestration screen
                      </button>
                    </ButtonField>
                  </div>
                ) : (
                  <div className={style[FILE_UPLOAD_SECTION]}>
                    <DragAndDropFileUpload
                      register={register}
                      reset={clearContent}
                      setValue={setValue}
                      filesLimit={1}
                      fileName={UPDATE_CONTENT_FIELD_NAME}
                    />
                  </div>
                )}
              </div>
            </TabPanel>
          </Tabs>
        </div>
      </div>
    </CsbErrorBoundary>
  );
};

export const validateImportFile = async (
  file: File,
  selectedWorkflow: any
): Promise<string | null> => {
  if (!file || !file.name) {
    return 'No file provided';
  }
  if (!isCorrectFileType(file)) {
    return 'Invalid file format selected';
  }
  const fileTagName = await xmlFileReader(file);
  if (selectedWorkflow) {
    const fileName = file.name.toLowerCase().replace('.xml', '');
    const orchestrationName = selectedWorkflow.name?.toLowerCase();
    if (
      fileName !== orchestrationName ||
      fileTagName !== selectedWorkflow.name
    ) {
      return 'Orchestration template name does not match the selected orchestration name';
    }
  }
  return null;
};

export const validateNewImportFile = async (
  file: File,
  workflowsNames: string[]
): Promise<string | null> => {
  if (!file || !file.name) {
    return 'No file provided';
  }
  if (!isCorrectFileType(file)) {
    return 'Invalid file format selected';
  }
  const fileTagName = await xmlFileReader(file);
  if (
    workflowsNames.includes(file.name.replace('.xml', '')) ||
    workflowsNames.includes(fileTagName)
  ) {
    return 'Orchestration with the same name already exists';
  }
  return null;
};

export default ImportOrchestrationModal;
